import { MapPin, Phone, Clock, Instagram, MessageCircle, Navigation } from "lucide-react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingButtons from "../components/FloatingButtons";
import PageBanner from "../components/PageBanner";
import Seo from "../components/Seo";
import { instagramGallery } from "../data/siteData";
import { useZeoContent } from "../lib/ZeoContentContext";
import { optimizeImg } from "../lib/img";

export default function Store() {
  const { business } = useZeoContent();
  const infoCards = [
    { icon: MapPin, title: "Adres", lines: [business.address] },
    { icon: Clock, title: "Çalışma Saatleri", lines: [business.hours, "Yılın her günü açığız"] },
    { icon: Phone, title: "Telefon & WhatsApp", lines: [business.phone] },
  ];
  const mapQuery = encodeURIComponent(business.address);

  return (
    <div className="min-h-screen bg-white text-[#222222]" data-testid="store-page">
      <Seo
        title="Mağazamız"
        description={`Frida Çiçekçilik atölyemizi ziyaret edin: ${business.address}. ${business.hours}. Aynı gün teslimat için telefon, WhatsApp veya web sitemizden sipariş verebilirsiniz.`}
        path="/magazamiz"
      />
      <Header />

      <PageBanner
        eyebrow="Kurumsal"
        title="Mağazamız"
        subtitle="Dörtyol'daki atölyemizde sizi ağırlamaktan mutluluk duyarız. Aynı gün teslimat için siparişlerinizi telefon, WhatsApp veya web sitemiz üzerinden alabiliriz."
        breadcrumb={[{ label: "Mağazamız" }]}
      />

      <section className="py-16 md:py-20 bg-white">
        <div className="frida-container grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-5 flex flex-col gap-5">
            {infoCards.map((c) => (
              <div key={c.title} className="bg-[#F8F8F8] rounded-3xl p-7 flex items-start gap-4" data-testid={`store-info-${c.title}`}>
                <div className="w-12 h-12 rounded-2xl bg-white border border-[#EAEAEA] flex items-center justify-center shrink-0">
                  <c.icon className="w-5 h-5 text-[#A85C34]" />
                </div>
                <div>
                  <h3 className="font-serif text-lg text-[#222222] mb-1">{c.title}</h3>
                  {c.lines.map((l, i) => (
                    <p key={i} className="text-[#555555] text-sm leading-relaxed">{l}</p>
                  ))}
                </div>
              </div>
            ))}

            <div className="bg-[#222222] rounded-3xl p-7">
              <h3 className="font-serif text-lg text-white mb-4">Bizi Takip Edin</h3>
              <div className="flex items-center gap-3">
                <a
                  href={business.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="w-11 h-11 rounded-full bg-white/10 hover:bg-[#D4A15C] hover:text-[#222222] flex items-center justify-center transition-colors"
                >
                  <Instagram className="w-4 h-4 text-white" />
                </a>
                <a
                  href={business.social.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp"
                  className="w-11 h-11 rounded-full bg-white/10 hover:bg-[#25D366] flex items-center justify-center transition-colors"
                >
                  <MessageCircle className="w-4 h-4 text-white" />
                </a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div
              className="relative w-full h-[360px] md:h-[480px] lg:h-full rounded-3xl overflow-hidden border border-[#EAEAEA] bg-[#F8F8F8] flex items-center justify-center text-center p-10"
              data-testid="store-map"
            >
              <div
                className="absolute inset-0 opacity-[0.06]"
                style={{
                  backgroundImage:
                    "radial-gradient(circle, #A85C34 1.5px, transparent 1.5px)",
                  backgroundSize: "22px 22px",
                }}
              />
              <div className="relative">
                <div className="w-16 h-16 rounded-full bg-[#A85C34]/10 flex items-center justify-center mx-auto mb-6">
                  <MapPin className="w-7 h-7 text-[#A85C34]" />
                </div>
                <h3 className="font-serif text-2xl text-[#222222] mb-2">Bizi Ziyaret Edin</h3>
                <p className="text-[#555555] max-w-xs mx-auto mb-7">{business.address}</p>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${mapQuery}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#A85C34] hover:bg-[#8B4726] text-white rounded-full font-medium transition-colors"
                  data-testid="store-map-directions"
                >
                  <Navigation className="w-4 h-4" />
                  Google Haritalar'da Yol Tarifi Al
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-[#F8F8F8]">
        <div className="frida-container">
          <div className="text-center max-w-xl mx-auto mb-10">
            <div className="text-xs tracking-[0.25em] uppercase text-[#A85C34] font-medium mb-3">
              · Atölyeden ·
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#222222] tracking-tight">
              Mağazamızdan Kareler
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {instagramGallery.map((img, i) => (
              <div key={i} className="aspect-square rounded-2xl overflow-hidden bg-[#EAEAEA]">
                <img
                  src={optimizeImg(img, 350)}
                  alt={`Frida Çiçekçilik mağaza ${i + 1}`}
                  width="350"
                  height="350"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
}
